import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MetricCard from './components/MetricCard';
import UsageChart from './components/UsageChart';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import SystemStatus from './components/SystemStatus';
import TopVINsWidget from './components/TopVINsWidget';

// Import localStorage services
import {
  getSession,
  calculateDashboardMetrics,
  calculateTopVins,
  getRecentActivities,
  getDailyQueryCounts,
  getTodayQueries
} from '../../services/localStorage';

const DashboardOverview = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [topVins, setTopVins] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [chartData, setChartData] = useState([]);

  // Get user role from session
  const session = getSession();
  const userRole = session?.role || 'admin';

  // Load dashboard data from localStorage
  const loadDashboardData = () => {
    const dashboardMetrics = calculateDashboardMetrics();
    const topVinsData = calculateTopVins(5);
    const activities = getRecentActivities(10);
    const dailyData = getDailyQueryCounts(7);

    setMetrics(dashboardMetrics);
    setTopVins(topVinsData);
    setRecentActivities(activities);
    setChartData(dailyData);
    setLastRefresh(new Date());
  };

  // Initial load and auto-refresh every 60 seconds
  useEffect(() => {
    loadDashboardData();

    const interval = setInterval(() => {
      loadDashboardData();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Calculate metrics from localStorage data
  const todayQueries = getTodayQueries();
  const hasData = todayQueries.length > 0;

  // Format currency helper
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(value);
  };

  // Build metrics data from calculated values
  const metricsData = [
    {
      title: "Consultas Diarias",
      value: metrics?.dailyQueries?.toString() || "0",
      subtitle: "de 50 límite por usuario",
      icon: "Search",
      trend: hasData ? "up" : "stable",
      trendValue: hasData ? `+${metrics?.dailyQueries || 0}` : "0",
      progress: Math.min(((metrics?.dailyQueries || 0) / 50) * 100, 100),
      variant: "default"
    },
    {
      title: "Tasa de Éxito",
      value: `${metrics?.successRate || 0}%`,
      subtitle: "Consultas exitosas hoy",
      icon: "CheckCircle",
      trend: parseFloat(metrics?.successRate || 0) >= 90 ? "up" : "down",
      trendValue: hasData ? `${metrics?.successRate}%` : "0%",
      variant: parseFloat(metrics?.successRate || 0) >= 90 ? "success" : "warning"
    },
    {
      title: "Usuarios Activos",
      value: "1",
      subtitle: "Sesión actual",
      icon: "Users",
      trend: "stable",
      trendValue: "0%",
      progress: 2,
      variant: "default"
    },
    {
      title: "Latencia Promedio",
      value: `${metrics?.avgLatency || 0}ms`,
      subtitle: "Tiempo de respuesta",
      icon: "Zap",
      trend: (metrics?.avgLatency || 0) < 1000 ? "up" : "down",
      trendValue: `${metrics?.avgLatency || 0}ms`,
      variant: (metrics?.avgLatency || 0) < 1000 ? "success" : "warning"
    },
    {
      title: "Valor Comercial Procesado",
      value: formatCurrency(metrics?.totalCommercialValue || 0),
      subtitle: "Valor total consultado hoy",
      icon: "DollarSign",
      trend: (metrics?.totalCommercialValue || 0) > 0 ? "up" : "stable",
      trendValue: (metrics?.totalCommercialValue || 0) > 0 ? "+1" : "0",
      variant: "success"
    },
    {
      title: "Consultas Restantes",
      value: Math.max(0, 50 - (metrics?.dailyQueries || 0)).toString(),
      subtitle: "Disponibles hoy",
      icon: "Clock",
      trend: "stable",
      trendValue: `${Math.max(0, 50 - (metrics?.dailyQueries || 0))} restantes`,
      variant: (50 - (metrics?.dailyQueries || 0)) > 10 ? "default" : "warning"
    }
  ];

  // Use chart data from localStorage or empty array
  const usageChartData = chartData.length > 0 ? chartData : [
    { fecha: "Hoy", consultas: 0, tasa_exito: 0, latencia: 0 }
  ];

  // Performance chart data
  const performanceChartData = chartData.length > 0 ? chartData.map(d => ({
    fecha: d.fecha,
    tasa_exito: parseFloat(d.tasa_exito) || 0,
    latencia: d.latencia || 0
  })) : [
    { fecha: "Hoy", tasa_exito: 0, latencia: 0 }
  ];

  // Format top VINs data for widget
  const topVinsData = topVins.map(item => ({
    vin: item.vin,
    brand: item.brand,
    model: item.model,
    year: item.year?.toString() || 'N/A',
    consultations: item.consultations,
    trend: "stable",
    trendValue: `${item.consultations}`
  }));

  // System alerts (static for now)
  const systemAlerts = hasData ? [] : [
    {
      message: "No hay consultas registradas. Realiza tu primera consulta VIN.",
      severity: "low",
      timestamp: new Date()
    }
  ];


  return (
    <>
      <Helmet>
        <title>Panel de Control - VIN Pro Platform</title>
        <meta name="description" content="Panel de control principal con métricas del sistema, actividad reciente y acciones rápidas para la plataforma VIN Pro." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />

        {/* Main content with proper sidebar spacing */}
        <main className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'ml-16' : 'ml-60'
        }`}>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Breadcrumbs />
                <h1 className="text-3xl font-semibold text-foreground">Panel de Control</h1>
                <p className="text-muted-foreground mt-1">
                  Monitoreo en tiempo real del sistema VIN Pro Platform
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                Última actualización: {lastRefresh?.toLocaleTimeString('es-MX')}
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {metricsData?.map((metric, index) =>
              <MetricCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                subtitle={metric?.subtitle}
                icon={metric?.icon}
                trend={metric?.trend}
                trendValue={metric?.trendValue}
                progress={metric?.progress}
                variant={metric?.variant} />

              )}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <UsageChart
                data={usageChartData}
                type="bar"
                title="Consultas VIN Diarias"
                height={300} />

              <UsageChart
                data={performanceChartData}
                type="line"
                title="Rendimiento del Sistema"
                height={300} />

            </div>

            {/* Activity and Actions Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <ActivityFeed activities={recentActivities} emptyMessage="No hay actividad reciente. Las consultas VIN aparecerán aquí." />
              </div>
              <div className="space-y-6">
                <QuickActions userRole={userRole} />
                <TopVINsWidget topVins={topVinsData} emptyMessage="Los VINs más consultados aparecerán aquí." />
              </div>
            </div>

            {/* System Status */}
            <SystemStatus
              systemHealth="healthy"
              activeSessions={23}
              serverLoad={35}
              lastUpdate={lastRefresh}
              alerts={systemAlerts} />

          </div>
        </main>

        <QuickActionButton />
      </div>
    </>);

};

export default DashboardOverview;